// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — dashboard BPMM (0735 · panel 2 columnas 0736)
// Layout 2 columnas espejo de M1.5: IZQ heatmap 7×5 + radar 7 + evolución +
// conclusión del ingeniero (colapsable) · DER cuestionario SIEMPRE visible
// (acordeón por pilar, scroll interno con position:absolute/inset:0). Debajo,
// análisis IA siempre visible (análisis → discrepancias → hoja de ruta).
// Consume /api/v1/process/madurez/*. Modelo BPMM (7 pilares, N1-N5) intacto.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import {
  fetchEvaluacionesBpmm,
  crearEvaluacionBpmm,
  sembrarPilaresBpmm,
  fetchHeatmapBpmm,
  aprobarEvaluacionBpmm,
  sembrarCuestionarioBpmm,
  guardarObservacionesBpmm,
  type EvaluacionBpmm,
  type HeatmapBpmm,
  type PilarEvaluacionBpmm,
  type NivelBpmm,
  type PilarBpmm,
} from './api-madurez-proceso';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { CuestionarioProcesoForm } from './components/CuestionarioProcesoForm';
import { MadurezProcesoAnalisisIA } from './components/MadurezProcesoAnalisisIA';
import { MadurezProcesoTimeline } from './components/MadurezProcesoTimeline';
import {
  NIVELES_BPMM,
  PREGUNTA_PILAR,
  MARCO_PILAR,
  descriptorCeldaProceso,
} from './descriptores-madurez-proceso';

const C = {
  a1: '#1F3864',
  a2: '#2F5496',
  ce: '#33BCE1',
  go: '#595959',
  gm: '#D9D9D9',
  gc: '#F2F2F2',
  w: '#FFF',
};
const VERDE = '#16A34A';
const AMBAR = '#E0A800';
const ROJO = '#DC2626';

const PILAR_ORDER: PilarBpmm[] = [
  'ESTRATEGIA',
  'PROCESOS',
  'METODO',
  'TECNOLOGIA',
  'PERSONAS',
  'GOBIERNO',
  'CULTURA',
];
const PILAR_LABEL: Record<PilarBpmm, string> = {
  ESTRATEGIA: 'Estrategia',
  PROCESOS: 'Procesos',
  METODO: 'Método',
  TECNOLOGIA: 'Tecnología',
  PERSONAS: 'Personas',
  GOBIERNO: 'Gobierno',
  CULTURA: 'Cultura',
};
const NIVELES: NivelBpmm[] = ['N1', 'N2', 'N3', 'N4', 'N5'];
const NIVEL_NUM: Record<NivelBpmm, number> = { N1: 1, N2: 2, N3: 3, N4: 4, N5: 5 };
const NIVEL_NOMBRE: Record<NivelBpmm, string> = {
  N1: 'Inicial',
  N2: 'Gestionado',
  N3: 'Definido',
  N4: 'Orientación por Proceso',
  N5: 'Excelencia',
};

const hexNivel = (n: NivelBpmm): string => {
  const v = NIVEL_NUM[n];
  return v >= 4 ? VERDE : v >= 2 ? AMBAR : ROJO;
};

const cardStyle: React.CSSProperties = {
  background: C.w,
  border: `0.5px solid ${C.gm}`,
  borderTop: `2px solid ${C.a1}`,
  borderRadius: 8,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};
const SecTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      color: C.a1,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      paddingBottom: 4,
      borderBottom: `1.5px solid ${C.a1}`,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);
const btn = (bg: string, color: string, border?: string): React.CSSProperties => ({
  fontSize: 12,
  fontWeight: 700,
  padding: '6px 12px',
  borderRadius: 6,
  border: border ?? 'none',
  background: bg,
  color,
  cursor: 'pointer',
  fontFamily: "'DM Sans',sans-serif",
});

export function MadurezProcesoDashboard({ embedded }: { embedded?: boolean }) {
  const { t } = useI18n();
  const mk = ((t.process as Record<string, unknown>)?.madurez ?? {}) as Record<string, unknown>;
  const s = (k: string, fb: string): string => (typeof mk[k] === 'string' ? (mk[k] as string) : fb);

  const [evaluaciones, setEvaluaciones] = useState<EvaluacionBpmm[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapBpmm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [vista, setVista] = useState<'real' | 'cuestionario'>('real');
  const [discrepKey, setDiscrepKey] = useState(0);
  // Conclusión del ingeniero de gobernanza (captura cualitativa editable, colapsable).
  const [mostrarConclusion, setMostrarConclusion] = useState(true);
  const [observaciones, setObservaciones] = useState('');
  const [savingObs, setSavingObs] = useState(false);
  const [obsGuardado, setObsGuardado] = useState(false);

  const cargarLista = useCallback(async (preferId?: string) => {
    const res = await fetchEvaluacionesBpmm();
    setEvaluaciones(res.data);
    const next = preferId ?? res.data[0]?.id ?? null;
    setSelectedId(next);
    return next;
  }, []);

  useEffect(() => {
    cargarLista()
      .then(() => setLoading(false))
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, [cargarLista]);

  useEffect(() => {
    if (!selectedId) return;
    fetchHeatmapBpmm(selectedId)
      .then((res) => setHeatmap(res.data))
      .catch((err: Error) => setError(err.message));
  }, [selectedId]);

  const crear = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await crearEvaluacionBpmm();
      await sembrarPilaresBpmm(data.id);
      await cargarLista(data.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [cargarLista]);

  const aprobar = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await aprobarEvaluacionBpmm(selectedId);
      const res = await fetchHeatmapBpmm(selectedId);
      setHeatmap(res.data);
      await cargarLista(selectedId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [selectedId, cargarLista]);

  // Sincroniza la conclusión guardada al cambiar de evaluación o tras refrescar la lista.
  const obsActual = evaluaciones.find((e) => e.id === selectedId)?.observaciones ?? '';
  useEffect(() => {
    setObservaciones(obsActual);
    setObsGuardado(false);
  }, [selectedId, obsActual]);

  const guardarObs = useCallback(async () => {
    if (!selectedId) return;
    setSavingObs(true);
    setError(null);
    setObsGuardado(false);
    try {
      await guardarObservacionesBpmm(selectedId, observaciones);
      setObsGuardado(true);
      await cargarLista(selectedId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingObs(false);
    }
  }, [selectedId, observaciones, cargarLista]);

  if (loading) {
    return (
      <div
        style={{
          height: embedded ? '100%' : 'calc(100vh - 124px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--wl-background, #ECEEF2)',
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        <LoadingSpinner text={s('loading', 'Cargando madurez de procesos…')} size="lg" />
      </div>
    );
  }

  const pilarMap = new Map<PilarBpmm, PilarEvaluacionBpmm>(
    (heatmap?.pilares ?? []).map((p) => [p.pilar, p]),
  );
  const nivelDe = (p: PilarEvaluacionBpmm | undefined): NivelBpmm | null =>
    !p ? null : vista === 'real' ? p.nivel : p.nivelCuestionario;
  const radarData = PILAR_ORDER.map((p) => {
    const n = nivelDe(pilarMap.get(p));
    return { pilar: PILAR_LABEL[p], nivel: n ? NIVEL_NUM[n] : 0 };
  });
  const seleccion = evaluaciones.find((e) => e.id === selectedId) ?? null;
  const aprobada = seleccion?.estado === 'APROBADA';
  const ng = heatmap?.evaluacion.nivelGlobal ?? null;
  const ngc = heatmap?.evaluacion.nivelGlobalCuestionario ?? null;
  const cob = heatmap?.evaluacion.coberturaModelo ?? null;
  const cobc = heatmap?.evaluacion.coberturaCuestionario ?? null;

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: embedded ? '100%' : 'calc(100vh - 124px)',
        padding: '10px 14px',
        overflow: 'auto',
        background: 'var(--wl-background, #ECEEF2)',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.a1 }}>
          {s('title', 'Madurez de procesos · BPMM')}
        </div>
        <div style={{ flex: 1 }} />
        {evaluaciones.length > 0 && (
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              fontSize: 12,
              padding: '5px 8px',
              borderRadius: 6,
              border: `1px solid ${C.gm}`,
              fontFamily: "'DM Sans',sans-serif",
              color: C.a1,
            }}
          >
            {evaluaciones.map((e) => (
              <option key={e.id} value={e.id}>
                {e.codigo ? `${e.codigo} · ` : ''}
                {new Date(e.fechaEvaluacion).toLocaleDateString()} · {e.tipoEjercicio} · {e.estado}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={crear}
          disabled={busy}
          style={{ ...btn(C.a1, C.w), opacity: busy ? 0.5 : 1 }}
        >
          {s('nuevaEvaluacion', 'Nueva evaluación')}
        </button>
        {seleccion && seleccion.estado === 'BORRADOR' && (
          <button
            onClick={aprobar}
            disabled={busy}
            style={{ ...btn(C.w, C.a1, `1px solid ${C.a1}`), opacity: busy ? 0.5 : 1 }}
          >
            {s('aprobar', 'Aprobar')}
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            background: '#FDECEC',
            border: `1px solid ${ROJO}`,
            color: '#7B0000',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {evaluaciones.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.go,
            gap: 8,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: C.a1 }}>
            {s('emptyTitle', 'Sin evaluaciones de madurez de procesos')}
          </div>
          <div style={{ fontSize: 13 }}>
            {s(
              'emptyDesc',
              'Crea una evaluación: el motor leerá el mapa de procesos y sembrará los 7 pilares BPMM.',
            )}
          </div>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div style={{ ...cardStyle, borderTopColor: ng ? hexNivel(ng) : C.a1 }}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('nivelGlobalReal', 'Nivel global · Real (mapa)')}
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: ng ? hexNivel(ng) : C.go,
                  lineHeight: 1.2,
                }}
              >
                {ng ? `${NIVEL_NUM[ng]} · ${NIVEL_NOMBRE[ng]}` : '—'}
              </div>
            </div>
            <div style={{ ...cardStyle, borderTopColor: ngc ? hexNivel(ngc) : C.a2 }}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('nivelGlobalCuest', 'Nivel global · Cuestionario')}
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: ngc ? hexNivel(ngc) : C.go,
                  lineHeight: 1.2,
                }}
              >
                {ngc ? `${NIVEL_NUM[ngc]} · ${NIVEL_NOMBRE[ngc]}` : '—'}
              </div>
            </div>
            <div style={cardStyle}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('coberturaModelo', 'Cobertura del modelo')}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.a1, lineHeight: 1.2 }}>
                {cob != null ? `${Math.round(Number(cob))}%` : '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.go, marginTop: 2 }}>
                {s('coberturaCuest', 'Cuestionario:')}{' '}
                {cobc != null ? `${Math.round(Number(cobc))}%` : '—'}
              </div>
            </div>
            <div style={cardStyle}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('estado', 'Estado')}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.a2, lineHeight: 1.4 }}>
                {seleccion?.estado ?? '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.go, marginTop: 2 }}>
                {s('codigo', 'Código:')} {seleccion?.codigo ?? '—'}
              </div>
            </div>
          </div>

          {/* Toggle lectura */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <span
              style={{ fontSize: 10, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
            >
              {s('lectura', 'Lectura:')}
            </span>
            {(['real', 'cuestionario'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVista(v)}
                style={{
                  ...btn(vista === v ? C.a1 : C.w, vista === v ? C.w : C.a1, `1px solid ${C.a1}`),
                  fontSize: 11,
                  padding: '4px 12px',
                }}
              >
                {v === 'real'
                  ? s('vistaReal', 'Real (mapa de procesos)')
                  : s('vistaCuestionario', 'Cuestionario')}
              </button>
            ))}
          </div>

          {/* ── Fila principal 2 columnas: IZQ (heatmap+radar+evolución+conclusión) · DER (cuestionario) ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr',
              gap: 10,
              alignItems: 'stretch',
              flexShrink: 0,
            }}
          >
            {/* COLUMNA IZQUIERDA */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              {/* Heatmap 7×5 */}
              <div style={cardStyle}>
                <SecTitle>{s('heatmapTitle', 'Heatmap 7 pilares × 5 niveles')}</SecTitle>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 6px', color: C.go }} />
                      {NIVELES.map((n) => (
                        <th
                          key={n}
                          title={NIVEL_NOMBRE[n]}
                          style={{
                            padding: '4px 6px',
                            color: C.a1,
                            fontWeight: 700,
                            fontSize: 10,
                            textAlign: 'center',
                          }}
                        >
                          {NIVEL_NUM[n]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PILAR_ORDER.map((p) => {
                      const pil = pilarMap.get(p);
                      const nivelActivo = nivelDe(pil);
                      const alcanzado = nivelActivo ? NIVEL_NUM[nivelActivo] : 0;
                      return (
                        <tr key={p}>
                          <td
                            title={`${PREGUNTA_PILAR[p]}\n\nMarco: ${MARCO_PILAR[p]}${
                              pil?.observaciones ? `\n\nNota: ${pil.observaciones}` : ''
                            }`}
                            style={{
                              padding: '4px 6px',
                              fontWeight: 600,
                              color: C.a1,
                              whiteSpace: 'nowrap',
                              cursor: 'help',
                            }}
                          >
                            {pil?.observaciones && (
                              <span style={{ color: C.ce, fontWeight: 800, marginRight: 4 }}>
                                ●
                              </span>
                            )}
                            {PILAR_LABEL[p]}
                          </td>
                          {NIVELES.map((n) => {
                            const val = NIVEL_NUM[n];
                            const esAlcanzado = !!nivelActivo && val === alcanzado;
                            let bg: string = C.gc;
                            if (nivelActivo) {
                              if (val === alcanzado) bg = hexNivel(nivelActivo);
                              else if (val < alcanzado) bg = `${hexNivel(nivelActivo)}33`;
                            }
                            return (
                              <td key={n} style={{ padding: 2 }}>
                                <div
                                  title={`${PILAR_LABEL[p]} — ${val} ${NIVEL_NOMBRE[n]}\n${descriptorCeldaProceso(p, n)}`}
                                  style={{
                                    height: 26,
                                    borderRadius: 4,
                                    background: bg,
                                    border: `0.5px solid ${C.gm}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: C.w,
                                    fontWeight: 800,
                                    fontSize: 10,
                                  }}
                                >
                                  {esAlcanzado ? val : ''}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Leyenda de niveles BPMM — semáforo (hover por celda para el detalle) */}
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.go,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 2,
                    }}
                  >
                    {s(
                      'niveles',
                      'Niveles BPMM (Robledo) — pasa el ratón por una celda para el detalle',
                    )}
                  </div>
                  {NIVELES_BPMM.map((nv) => (
                    <div
                      key={nv.nivel}
                      title={nv.resumen}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: 10,
                        cursor: 'help',
                      }}
                    >
                      <span
                        style={{
                          width: 20,
                          flexShrink: 0,
                          fontWeight: 800,
                          color:
                            nv.color === 'VERDE' ? VERDE : nv.color === 'AMARILLO' ? AMBAR : ROJO,
                        }}
                      >
                        {nv.etiqueta}
                      </span>
                      <span style={{ flexShrink: 0, fontWeight: 700, color: C.a1 }}>
                        {nv.nombre}
                      </span>
                      <span
                        style={{
                          color: C.go,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {nv.resumen}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Radar 7 pilares */}
              <div style={cardStyle}>
                <SecTitle>{s('radarTitle', 'Radar 7 pilares')}</SecTitle>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} outerRadius="72%">
                      <PolarGrid stroke={C.gm} />
                      <PolarAngleAxis dataKey="pilar" tick={{ fontSize: 9, fill: C.go }} />
                      <PolarRadiusAxis
                        domain={[0, 5]}
                        tickCount={6}
                        tick={{ fontSize: 8, fill: C.go }}
                      />
                      <Radar
                        dataKey="nivel"
                        stroke={C.a1}
                        fill={C.a2}
                        fillOpacity={0.45}
                        isAnimationActive={false}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Evolución del nivel global */}
              <div style={{ ...cardStyle, height: 240 }}>
                <SecTitle>{s('timeline', 'Evolución del nivel global')}</SecTitle>
                <MadurezProcesoTimeline
                  evaluaciones={evaluaciones}
                  emptyLabel={s('timelineEmpty', 'Aún no hay histórico suficiente.')}
                />
              </div>

              {/* Conclusión del ingeniero de gobernanza (colapsable) + notas por pilar (read-only) */}
              <div style={cardStyle}>
                <SecTitle>
                  {s('conclusionConsultor', 'Conclusión del ingeniero de gobernanza')}
                </SecTitle>
                <button
                  type="button"
                  onClick={() => setMostrarConclusion((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.a2,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  <span style={{ fontSize: 10 }}>{mostrarConclusion ? '▾' : '▸'}</span>
                  {s('conclusionConsultor', 'Conclusión del ingeniero de gobernanza')}{' '}
                  <span style={{ color: C.ce, fontWeight: 700 }}>
                    {s('opcional', '(opcional)')}
                  </span>
                </button>
                {mostrarConclusion && (
                  <>
                    {aprobada ? (
                      <>
                        <textarea
                          value={observaciones}
                          readOnly
                          rows={3}
                          style={{
                            width: '100%',
                            fontSize: 12,
                            fontFamily: "'DM Sans',sans-serif",
                            border: `1px solid ${C.gm}`,
                            borderRadius: 6,
                            padding: 8,
                            resize: 'vertical',
                            background: '#F7F7F7',
                            boxSizing: 'border-box',
                          }}
                        />
                        <div
                          style={{ fontSize: 11, color: C.go, marginTop: 4, fontStyle: 'italic' }}
                        >
                          🔒 {s('conclusionBloqueada', 'Diagnóstico aprobado · solo lectura')}
                        </div>
                      </>
                    ) : (
                      <>
                        <textarea
                          value={observaciones}
                          onChange={(e) => {
                            setObservaciones(e.target.value);
                            setObsGuardado(false);
                          }}
                          rows={3}
                          placeholder={s(
                            'conclusionPlaceholder',
                            'Tu lectura experta del contexto, más allá del dato objetivo…',
                          )}
                          style={{
                            width: '100%',
                            fontSize: 12,
                            fontFamily: "'DM Sans',sans-serif",
                            border: `1px solid ${C.gm}`,
                            borderRadius: 6,
                            padding: 8,
                            resize: 'vertical',
                            background: C.w,
                            boxSizing: 'border-box',
                          }}
                        />
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}
                        >
                          <button
                            onClick={guardarObs}
                            disabled={savingObs}
                            style={{ ...btn(C.a2, C.w), opacity: savingObs ? 0.5 : 1 }}
                          >
                            {s('guardarConclusion', 'Guardar conclusión')}
                          </button>
                          {obsGuardado && (
                            <span style={{ color: VERDE, fontSize: 12 }}>
                              {s('guardado', 'Guardado')}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                    {/* Notas por pilar (read-only) */}
                    {PILAR_ORDER.some((p) => pilarMap.get(p)?.observaciones) && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          marginTop: 12,
                          paddingTop: 10,
                          borderTop: `1px solid ${C.gc}`,
                        }}
                      >
                        {PILAR_ORDER.map((p) => {
                          const pil = pilarMap.get(p);
                          if (!pil?.observaciones) return null;
                          const col =
                            pil.color === 'VERDE'
                              ? VERDE
                              : pil.color === 'ROJO'
                                ? ROJO
                                : pil.color === 'AMARILLO'
                                  ? AMBAR
                                  : C.gm;
                          return (
                            <div
                              key={p}
                              style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: col,
                                  marginTop: 5,
                                  flexShrink: 0,
                                }}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: C.a1 }}>
                                  {PILAR_LABEL[p]}
                                  <span style={{ fontWeight: 600, color: C.go, marginLeft: 6 }}>
                                    {pil.nivel
                                      ? `${NIVEL_NUM[pil.nivel]} ${NIVEL_NOMBRE[pil.nivel]} · real`
                                      : ''}
                                    {pil.nivelCuestionario
                                      ? ` · ${NIVEL_NUM[pil.nivelCuestionario]} ${NIVEL_NOMBRE[pil.nivelCuestionario]} · cuestionario`
                                      : ''}
                                  </span>
                                </div>
                                <div style={{ fontSize: 11, color: '#000', lineHeight: 1.5 }}>
                                  {pil.observaciones}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* COLUMNA DERECHA: cuestionario SIEMPRE visible, acotado a la altura de la izquierda.
                position:absolute/inset:0 saca su contenido del flujo → la fila la fija la izquierda
                (stretch) y el formulario hace scroll dentro. */}
            <div style={{ position: 'relative', minHeight: 0 }}>
              <div
                style={{
                  ...cardStyle,
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <SecTitle>{s('cuestionarioTitle', 'Cuestionario BPMM')}</SecTitle>
                {selectedId ? (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <CuestionarioProcesoForm
                      key={selectedId}
                      evaluacionId={selectedId}
                      readOnly={aprobada}
                      onSaved={() => {
                        const evalId = selectedId;
                        setDiscrepKey((k) => k + 1);
                        void sembrarCuestionarioBpmm(evalId)
                          .then(() => fetchHeatmapBpmm(evalId))
                          .then((r) => setHeatmap(r.data))
                          .catch((err: Error) => setError(err.message));
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: C.go,
                      fontSize: 11,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: 8,
                    }}
                  >
                    {s(
                      'cuestionarioHint',
                      'Selecciona una evaluación para responder y triangular el dato objetivo con la percepción.',
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Análisis IA BPMM SIEMPRE visible (análisis → discrepancias → hoja de ruta) ── */}
          {selectedId && seleccion && (
            <div style={{ flexShrink: 0 }}>
              <MadurezProcesoAnalisisIA
                key={selectedId}
                evaluacionId={selectedId}
                estadoEvaluacion={seleccion.estado}
                observacionesIniciales={seleccion.observaciones ?? null}
                discrepKey={discrepKey}
                onChanged={() => void cargarLista(selectedId).catch(() => undefined)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
